import { Link } from "react-router-dom";
import type { ComicApp } from "@/mocks/apps";
import { comicApps } from "@/mocks/apps";
import Rating from "@/components/base/Rating";

function ReviewLine({ title, lines, icon, tone }: { title: string; lines: string[]; icon: string; tone: string }) {
  return (
    <div className="rounded-2xl border border-background-200/80 bg-background-100 p-6">
      <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}>
          <i className={`${icon} text-background-50`} aria-hidden="true"></i>
        </span>
        {title}
      </h2>
      <ul className="mt-4 space-y-3">
        {lines.map((line) => (
          <li key={line} className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
            <i className="ri-checkbox-circle-fill mt-0.5 text-accent-500" aria-hidden="true"></i>
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AppReview({ app }: { app: ComicApp }) {
  const related = comicApps.filter((a) => a.category === app.category && a.id !== app.id).slice(0, 3);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-16 pt-4 md:px-6">
      <div className="overflow-hidden rounded-3xl border border-background-200/80 bg-background-100">
        <div className="flex flex-col gap-6 p-6 md:flex-row md:p-8">
          <div className="mx-auto w-40 shrink-0 overflow-hidden rounded-xl bg-background-200 md:mx-0 md:w-44">
            <img src={app.cover} alt={`${app.name}漫画软件应用封面`} title={`${app.name}漫画软件应用`} className="aspect-[3/4] w-full object-cover" />
          </div>
          <div className="flex flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-2">
              <span className="whitespace-nowrap rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-bold text-secondary-900">{app.category}</span>
              <span className="whitespace-nowrap rounded-md bg-background-200 px-2 py-0.5 text-xs text-foreground-600">{app.price}</span>
            </div>
            <h1 className="mt-3 font-heading text-3xl text-foreground-950">{app.name}漫画软件应用评测</h1>
            <p className="mt-1 text-sm text-foreground-600">{app.tagline}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Rating value={app.rating} />
              <span className="text-sm text-foreground-600">{app.rating} 分</span>
              <span className="flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-2.5 py-0.5 text-sm font-black text-background-50">
                综合 {app.score} 分
              </span>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-smartphone-line text-primary-500" aria-hidden="true"></i>
                支持平台：{app.platforms.join(" / ")}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-price-tag-3-line text-primary-500" aria-hidden="true"></i>
                适合：{app.bestFor}
              </span>
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {app.tags.map((t) => (
                <span key={t} className="whitespace-nowrap rounded-md bg-accent-100 px-2.5 py-1 text-xs font-bold text-accent-900">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-lg font-bold text-foreground-950">一句话点评</h2>
        <p className="mt-3 rounded-2xl border-l-4 border-primary-500 bg-background-100 p-5 text-base leading-relaxed text-foreground-800">
          {app.summary}
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
        <ReviewLine title="核心亮点" lines={app.pros} icon="ri-thumb-up-fill" tone="bg-accent-500" />
        <ReviewLine title="需要注意" lines={app.cons} icon="ri-information-fill" tone="bg-secondary-500" />
      </div>

      <div className="mt-5 rounded-2xl border border-background-200/80 bg-background-100 p-6">
        <h2 className="text-lg font-bold text-foreground-950">适合谁用</h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700">
          {app.bestFor}。如果你正好符合这一画像，{app.name}大概率能带来不错的漫画阅读体验；如果不是很确定，也可以先对照本站的漫画软件应用排行再做决定。
        </p>
      </div>

      {related.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-bold text-foreground-950">同分类的漫画软件应用推荐</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {related.map((r) => (
              <Link key={r.id} to={`/reviews/${r.id}`} className="group flex items-center gap-3 overflow-hidden rounded-2xl border border-background-200/80 bg-background-100 p-3 transition-colors hover:border-primary-300">
                <div className="h-16 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img src={r.cover} alt={`${r.name}漫画软件应用`} title={`${r.name}漫画软件应用`} className="h-full w-full object-cover" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600">{r.name}</p>
                  <p className="mt-0.5 line-clamp-1 text-xs text-foreground-600">{r.tagline}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <Rating value={r.rating} size="text-xs" />
                    <span className="text-xs text-foreground-500">{r.rating} 分</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}